import { useAdminFunnel, useAdminOverviewKpis, useAdminPayoutQueue, useAdminReviewQueue, useAdminFraudSignals } from '../lib/api'
import { fmtEur, fmtNum } from '../lib/format'
import { Link } from 'react-router-dom'

const STEP_LABEL: Record<string, string> = {
  registered:       'Sign-ups',
  emailVerified:    'Verified email',
  paid:             'Paid challenge',
  p1Started:        'Started P1',
  p1Passed:         'Passed P1',
  p2Passed:         'Passed P2',
  fundedInvited:    'Funded',
  payoutRequested:  'Received payout',
}

export function AdminOverviewPage() {
  const kpis    = useAdminOverviewKpis()
  const funnel  = useAdminFunnel()
  const payouts = useAdminPayoutQueue({ status: 'pending', limit: 50, offset: 0 })
  const reviews = useAdminReviewQueue({ status: 'pending', limit: 50, offset: 0 })
  const fraud   = useAdminFraudSignals({ status: 'unresolved', limit: 50, offset: 0 })

  return (
    <>
      <header className="page-header">
        <div className="page-header__title-row">
          <h1 className="page-header__title">Dashboard</h1>
          <span className="page-header__subtitle">Operational overview — last 30 days.</span>
        </div>
      </header>

      {kpis.isError && (
        <div className="auth-card__error" role="alert" style={{ display: 'flex', marginBottom: 'var(--space-4)' }}>
          Failed to load KPI data.
        </div>
      )}

      {/* KPI strip */}
      <section className="dashboard-grid dashboard-grid--cols-4" style={{ marginBottom: 'var(--space-6)' }}>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Active funded</span></header>
          <div className="kpi-card__value">{kpis.data ? fmtNum(kpis.data.activeFundedTraders, 0) : '—'}</div>
          <p className="kpi-card__detail">Traders in P4</p>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Payouts pending</span></header>
          <div className="kpi-card__value">{kpis.data ? fmtEur(kpis.data.pendingPayouts) : '—'}</div>
          <p className="kpi-card__detail">{(payouts.data?.payouts?.length ?? 0)} request{(payouts.data?.payouts?.length ?? 0) === 1 ? '' : 's'}</p>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Fraud flags</span></header>
          <div className="kpi-card__value" style={{ color: (kpis.data?.openFraudSignals ?? 0) > 0 ? 'var(--admin-accent-text)' : undefined }}>
            {kpis.data ? fmtNum(kpis.data.openFraudSignals, 0) : '—'}
          </div>
          <p className="kpi-card__detail">Unresolved signals</p>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Revenue (30d)</span></header>
          <div className="kpi-card__value">{kpis.data ? fmtEur(kpis.data.revenue) : '—'}</div>
          <p className="kpi-card__detail" style={{ color: 'var(--color-success-text)' }}>Window 30d</p>
        </article>
      </section>

      {/* Review queues */}
      <h2 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)', margin: '0 0 var(--space-3)', color: 'var(--text-primary)' }}>
        Review queues
      </h2>
      <section className="queue-grid" style={{ marginBottom: 'var(--space-6)' }}>
        <QueueCard to="/payouts" label="Payouts" count={payouts.data?.payouts?.length ?? 0}
          detail={(payouts.data?.payouts?.length ?? 0) > 0 ? 'Pending requests' : 'No pending requests'}
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10 h18 M7 15 h4"/></svg>} />
        <QueueCard to="/fraud" label="Fraud flags" count={fraud.data?.signals?.length ?? 0}
          detail={(fraud.data?.signals?.length ?? 0) > 0 ? 'Unresolved' : 'All clear'}
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9 v4 M12 17 v.01 M10.29 3.86 L1.82 18 a2 2 0 0 0 1.71 3 h16.94 a2 2 0 0 0 1.71 -3 L13.71 3.86 a2 2 0 0 0 -3.42 0 z"/></svg>} />
        <QueueCard to="/reviews" label="KYC reviews" count={reviews.data?.reviews?.length ?? 0}
          detail={(reviews.data?.reviews?.length ?? 0) > 0 ? 'Awaiting reviewer' : 'No pending reviews'}
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M5 18 q4 -4 8 0"/></svg>} />
      </section>

      {/* Cohort funnel */}
      <section className="funnel" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="funnel__head">
          <h3 className="funnel__title">Cohort funnel — last 30 days</h3>
          <span className="funnel__period">
            {funnel.data ? new Date(funnel.data.windowFromIso).toLocaleDateString() + ' → ' + new Date(funnel.data.windowToIso).toLocaleDateString() : ''}
          </span>
        </div>
        <div className="funnel__bars">
          {funnel.data && (() => {
            const steps = funnel.data.steps
            const max = Math.max(1, ...steps.map((s) => s.count))
            return steps.map((s) => {
              const pct = (s.count / max) * 100
              return (
                <div key={s.step} className="funnel__row">
                  <span className="funnel__row-label">{STEP_LABEL[s.step] ?? s.step}</span>
                  <div className="funnel__bar"><div className="funnel__bar-fill" style={{ width: pct.toFixed(1) + '%' }} /></div>
                  <span className="funnel__count">{fmtNum(s.count, 0)}</span>
                  <span className="funnel__pct">{pct.toFixed(1)}%</span>
                </div>
              )
            })
          })()}
        </div>
      </section>
    </>
  )
}

function QueueCard({ to, label, count, detail, icon }: {
  to: string; label: string; count: number; detail: string; icon: React.ReactNode
}) {
  const urgent = count >= 5
  return (
    <Link to={to} className={'queue-card' + (urgent ? ' queue-card--urgent' : '')}>
      <div className="queue-card__head">
        <span className="queue-card__label">{label}</span>
        <span className="queue-card__icon">{icon}</span>
      </div>
      <div className="queue-card__count">{count}</div>
      <span className="queue-card__detail">{detail}</span>
      <span className="queue-card__cta">Review queue →</span>
    </Link>
  )
}
